import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

/** Merge class names, letting later Tailwind utilities win over earlier ones. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * A non-breaking space, used both to group thousands and to hold the R to its
 * number. A normal space lets an amount break across two lines in a narrow
 * cell, where `R 50` above `000.00` reads as two different figures.
 */
const NBSP = " "

/** `always` keeps `.00`; `auto` drops it when there are no cents to show. */
type CentsPolicy = "always" | "auto"

/**
 * South African rand, the only currency this product deals in.
 *
 * `R 50 000.00`: a space groups the thousands, a full stop separates the cents.
 *
 * The space is the South African convention and the comma this used to print
 * was not. It came from Oliver's build specification, was published to the
 * design side as a commitment, and was then defended in the runbook as the
 * correct answer, which is how a wrong value survives three milestones. Dawid
 * flagged it on 2026-07-28. The full stop is the commercial form rather than
 * the SANS 920 comma, chosen because it is what Excel and the banks show these
 * users; that choice is his and is recorded in the manual.
 *
 * Still built by hand rather than through `toLocaleString("en-ZA")`, and that
 * part was always right: locale data would give `R 50 000,00` under Node's ICU
 * and something else elsewhere, and a promised format must not vary with
 * whichever ICU the runtime happens to ship. Note the difference between the
 * two mistakes. The mechanism was sound; only the value was wrong.
 *
 * Amounts arrive as decimal strings so the wire cannot lose precision to a
 * float, hence the string input.
 */
export function formatRands(
  amount: string | number | null | undefined,
  options: { cents?: CentsPolicy } = {},
) {
  const { symbol, digits } = splitRands(amount, options)
  return `${symbol}${NBSP}${digits}`
}

/**
 * The same format, handed back in its two halves.
 *
 * A money column puts the symbol against the left edge of the cell and the
 * digits against the right, so every R sits under the one above it while the
 * numbers still align digit for digit. That needs the two pieces separately;
 * `<Money>` in `components\money.tsx` is what assembles them.
 *
 * A minus sign travels with the digits rather than with the R, so that a
 * negative row does not push its symbol out of the column. No amount in this
 * product goes negative today, but there has to be one rule for both callers.
 */
export function splitRands(
  amount: string | number | null | undefined,
  { cents = "always" }: { cents?: CentsPolicy } = {},
): { symbol: string; digits: string } {
  const value =
    amount === null || amount === undefined || amount === ""
      ? 0
      : typeof amount === "string"
        ? Number(amount)
        : amount
  const safe = Number.isNaN(value) ? 0 : value

  const [whole = "0", fraction = "00"] = Math.abs(safe).toFixed(2).split(".")
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP)
  const tail = cents === "auto" && fraction === "00" ? "" : `.${fraction}`

  return { symbol: "R", digits: `${safe < 0 ? "-" : ""}${grouped}${tail}` }
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

/**
 * Dates display day-month-year; the API exchanges ISO.
 * Never use this on a value the server expects back.
 *
 * Built by hand for the same reason as the currency: a displayed format that
 * shifts with the runtime's locale data is not a format we can promise.
 */
export function formatDate(iso: string | null | undefined) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const day = String(date.getDate()).padStart(2, "0")
  return `${day} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`
}

export function formatDateTime(iso: string | null | undefined) {
  if (!iso) return ""
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const hours = String(date.getHours()).padStart(2, "0")
  const minutes = String(date.getMinutes()).padStart(2, "0")
  return `${formatDate(iso)}, ${hours}:${minutes}`
}

/**
 * Age is not a field on the employee: it is derived from date of birth.
 *
 * The parts are read out of the string rather than out of a `Date`, because
 * `new Date("1996-07-28")` is defined to parse a date-only string as **UTC**
 * midnight while `getDate()` reads it back in **local** time. West of
 * Greenwich those disagree, and every age computed near a birthday comes out
 * a year wrong. A date of birth is a calendar date with no instant attached,
 * which is the same reason the backend keeps it a `DateField`; converting it
 * to an offset here would reintroduce exactly what that decision avoids.
 */
export function ageFrom(dateOfBirth: string | null | undefined) {
  if (!dateOfBirth) return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateOfBirth)
  if (!match) return null
  const [, year, month, day] = match.map(Number) as [number, number, number, number]

  const now = new Date()
  let age = now.getFullYear() - year
  const monthDelta = now.getMonth() + 1 - month
  if (monthDelta < 0 || (monthDelta === 0 && now.getDate() < day)) age -= 1
  return age
}
